import { useAppDispatch } from '../../../../../../../hooks/useAppDispatch'
import React, { useEffect, useState } from 'react'
import { IS_RULE_ACTIVE, RULE_NAME } from '../../../../utils/consts'
import { PATH_JOINER } from '../../../../../utils/getConfigBitPath'
import styles from '../../../../Styles/index.module.scss'
import { RuleNameInput } from '../../../BlockArrayCards/RuleNameInput/RuleNameInput'
import { OnOffButton } from '../../../../../../buttons/OnOffButton/OnOffButton'
import { withTooltip } from '../../../../../../../utils/withTooltip'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowRightArrowLeft } from '@fortawesome/free-solid-svg-icons/faArrowRightArrowLeft'
import RemoveItemBtn from '../../../../../../buttons/RemoveItemBtn/RemoveItemBtn'
import {
    hideModal,
    showModal,
} from '../../../../../../../store/actions/modalActions'
import { ModalTypes } from '../../../../../../../types/modals'
import { buildRuleCard } from '../buildRuleCard'
import {
    changeRuleCardState,
    modifyRuleCardConfig,
} from '../../../../../../../store/actions/ruleCardActions'
import { configsActions } from '../../../../../../../store/slices/configsSlice'

interface Props {
    schema: any
    config: any
    dataPath: string[]
    pathWithElementID: string | undefined
    element_id: string
    index: number
    focusIndex?: number
    isNewCardAdded?: boolean
    rulesBlocks?: any[]
    inCard?: boolean
    isParentCardActive?: boolean
    inRuleModal?: boolean
    className?: string
    setCardToDeleteElementID?: React.Dispatch<
        React.SetStateAction<null | string>
    >
}

export const RuleCard = (props: Props) => {
    const {
        schema = {},
        config = {},
        rulesBlocks,
        dataPath,
        pathWithElementID,
        element_id,
        index,
        focusIndex,
        isNewCardAdded,
        inCard,
        isParentCardActive,
        inRuleModal,
        className,
        setCardToDeleteElementID,
    } = props

    const dispatch = useAppDispatch()
    const [cardStyle, setCardStyle] = useState('')
    const isOrderUserDefined = schema.is_order_user_defined
    const cardScheme = schema.children?.children || schema.children
    const cardSchemeKeys = Object.keys(cardScheme || {})
    const cardSchemeNames = cardSchemeKeys.filter(
        (name) => name !== RULE_NAME && name !== IS_RULE_ACTIVE
    )

    const ruleName = (config[RULE_NAME] as string) ?? ''
    const hasRuleName = cardSchemeKeys.includes(RULE_NAME)
    const isRuleNameRequired =
        hasRuleName && !!cardScheme[RULE_NAME]?.is_required
    const hasRuleState = cardSchemeKeys.includes(IS_RULE_ACTIVE)
    const isRuleActive = inCard
        ? isParentCardActive
        : ((config[IS_RULE_ACTIVE] as boolean) ??
          cardScheme[IS_RULE_ACTIVE]?.default ??
          true)

    const path = [...(dataPath || []), index.toString()]
    const position = index + 1

    // pathWithElementID either initiated here by replacing item index with item element_id or modified by complementing with the element_id of the current item instead of index
    const initialPathWithElementID: string[] = pathWithElementID
        ? [...pathWithElementID?.split(PATH_JOINER), element_id]
        : [...(dataPath ?? []), element_id]

    const onDeleteCardHandle = (): void => {
        if (element_id && setCardToDeleteElementID) {
            setCardToDeleteElementID(element_id)
        }
    }

    const handleChangeRuleState = () => {
        dispatch(changeRuleCardState(!isRuleActive))
    }

    const handleMove = (data: any[]) => {
        dispatch(modifyRuleCardConfig({ path: dataPath, data }))
        dispatch(hideModal())
    }

    const handleMoveRule = () => {
        dispatch(
            showModal(ModalTypes.MOVE_RULE_MODAL, {
                rules: rulesBlocks,
                onSave: handleMove,
            })
        )
    }

    useEffect(() => {
        if (index === focusIndex && isNewCardAdded) {
            setCardStyle('card')
            setTimeout(() => {
                setCardStyle('')
            }, 1500)
        }
    }, [index, focusIndex, isNewCardAdded])

    // TODO check suggested value and other rule name related features
    useEffect(() => {
        if (isRuleNameRequired ?? ruleName) {
            dispatch(
                configsActions.addSuggestedValue({
                    suggestedValuesPath: dataPath.join(PATH_JOINER),
                    value: ruleName,
                })
            )
        }
    }, [ruleName]) // eslint-disable-line

    return (
        <div
            className={`${styles[cardStyle]} d-flex flex-wrap justify-content-between position`}
        >
            <div
                className={`rounded w-100 array-item-header p-1 border ${
                    isRuleActive ? 'app-bg-middle-blue' : 'rule-inactive'
                } text-capital`}
            >
                <div className="px-2 py-1 text-light d-flex gap-2">
                    {position}
                    <>
                        {hasRuleName && (
                            <RuleNameInput
                                ruleName={ruleName}
                                dataPath={path.join(PATH_JOINER)}
                                pathWithElementID={initialPathWithElementID.join(
                                    PATH_JOINER
                                )}
                                isRequired={cardScheme[RULE_NAME]?.is_required}
                                isRuleActive={isRuleActive}
                                className={className}
                                isRuleCard
                            />
                        )}
                        {hasRuleState && (
                            <OnOffButton
                                onClick={handleChangeRuleState}
                                isButtonOn={isRuleActive}
                                className={`${styles.button} ${styles.onOffBtn} ${className}`}
                            />
                        )}
                        {isOrderUserDefined &&
                            !inRuleModal &&
                            // @ts-ignore
                            rulesBlocks?.length > 1 &&
                            withTooltip(
                                <button
                                    className={`${styles.button} ${styles.moveBtn}`}
                                    onClick={handleMoveRule}
                                >
                                    <FontAwesomeIcon
                                        rotation={90}
                                        icon={faArrowRightArrowLeft}
                                    />
                                </button>,
                                'Move rule',
                                'move-rule-key'
                            )}
                        {!inRuleModal && (
                            <RemoveItemBtn
                                data={{
                                    index,
                                    body: `Remove item ${position} ?`,
                                    onSubmit: onDeleteCardHandle,
                                }}
                            />
                        )}
                    </>
                </div>
            </div>
            <div className="p-3 w-100">
                {cardSchemeNames.map((keyName: string, i: number) => (
                    <div key={i}>
                        {buildRuleCard(
                            path.join(PATH_JOINER),
                            keyName,
                            cardScheme[keyName],
                            config,
                            'block_array',
                            false,
                            3,
                            element_id,
                            true,
                            isRuleActive,
                            initialPathWithElementID.join(PATH_JOINER)
                        )}
                    </div>
                ))}
            </div>
        </div>
    )
}
